import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { DashboardMode, DrilldownFilterPresetDto } from "@nexus/contracts";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { filtersKey } from "@/features/dashboard/lib/filters";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import {
  applyDrilldownFilterPreset,
  drilldownPresetMatchesCurrentFilters,
  selectDrilldownDraftKey,
} from "@/features/drilldown/lib/filter-preset-utils";
import { useDrilldownFiltersStore } from "@/features/drilldown/stores/drilldownFilters.store";
import { drilldownFilterPresetsQueryOptions } from "@/lib/queries/filter-presets.queries";
import { FilterPresetSelectorCore } from "./FilterPresetSelectorCore";

interface DrilldownFilterPresetSelectorProps {
  mode: DashboardMode;
  filtersHydrated?: boolean;
  onPresetApplied?: (preset: DrilldownFilterPresetDto) => void;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  selectPresetId?: string | null;
}

export function DrilldownFilterPresetSelector({
  mode,
  filtersHydrated = true,
  onPresetApplied,
  applySignal,
  onPendingPresetChange,
  selectPresetId = null,
}: DrilldownFilterPresetSelectorProps) {
  const { t } = useTranslation();
  const canListPresets = useIamPermission(usePermissions().DRILLDOWN_VIEW_PAGE);
  const { data: presets = [] } = useQuery({
    ...drilldownFilterPresetsQueryOptions(mode),
    enabled: canListPresets,
  });
  const drilldownDraftKey = useDrilldownFiltersStore((s) => selectDrilldownDraftKey(s, mode));
  const globalDraftKey = useDashboardFiltersStore((s) =>
    filtersKey(s.mode === "competitors" ? s.competitorFilters : s.myBrandFilters),
  );
  const filterSignature = `${drilldownDraftKey}\u0000${globalDraftKey}`;

  const labels = useMemo(
    () => ({
      placeholder: t("drilldown.filterPresets.placeholder", "Choose Preset"),
      triggerLabel: t("drilldown.filterPresets.triggerLabel", "Presets"),
      listHeading: t("drilldown.filterPresets.listHeading", "Presets"),
      searchPlaceholder: t("drilldown.filterPresets.searchPlaceholder", "Search presets"),
      emptyMessage: t("drilldown.filterPresets.empty", "No presets match"),
      listboxLabel: t("drilldown.filterPresets.listboxLabel", "Filter presets"),
      ariaLabel: t("drilldown.filterPresets.ariaLabel", "Filter presets"),
      deleteAria: t("drilldown.filterPresets.deleteAria", "Delete preset"),
    }),
    [t],
  );

  return (
    <FilterPresetSelectorCore<DrilldownFilterPresetDto, { name: string }>
      surface="drilldown"
      mode={mode}
      canListPresets={canListPresets}
      labels={labels}
      filtersHydrated={filtersHydrated}
      filterSignature={filterSignature}
      presets={presets}
      applyPreset={applyDrilldownFilterPreset}
      presetMatchesCurrent={drilldownPresetMatchesCurrentFilters}
      onPresetApplied={onPresetApplied}
      applySignal={applySignal}
      onPendingPresetChange={onPendingPresetChange}
      allowCreate={false}
      selectPresetId={selectPresetId}
    />
  );
}
